import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Image from 'next/image';
import Link from 'next/link';
import { ENDPOINTS } from '@/config/endpoints';
import { ThreeDots } from 'react-loader-spinner';
import Pagination from '@/component/shared/pagination';
import { SEODATA } from '@/config/seo_config';
import ResourcesLinks from '@/layout/resourcesLinks';
import Head from 'next/head';
import IMAGES from '@/public/assets/img';


export const metadata = {
  title: SEODATA.EVENTSLIST.title,
  description: SEODATA.EVENTSLIST.description,
  keywords: SEODATA.EVENTSLIST.keyword,
  alternates: {
    canonical: SEODATA.EVENTSLIST.url
  },
  
  openGraph: {
    title: SEODATA.EVENTSLIST.title,
    description: SEODATA.EVENTSLIST.description,
    url: SEODATA.EVENTSLIST.url,
    imagePath:IMAGES.HOMEIMG.bigLogoNew.src
  }
};


const News=()=> {
  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loader,setLoader] = useState(true);
  const [expandedPosts, setExpandedPosts] = useState([]);

    useEffect(() => {
    const fetchPosts = async () => {
      try{
        const res = await axios.get(ENDPOINTS.WORDPRESS_EVENTS_POST_LIST_WITH_IMAGE + `&page=${currentPage}`);
        setPosts(res.data);
        setLoader(false);
        const totalPosts = res.headers['x-wp-total'];
          const totalPages = res.headers['x-wp-totalpages'];
          setTotalPages(parseInt(totalPages));
      } catch (error) {
        console.error('Error:', error);
      }
     
    };

    fetchPosts();
    
  }, [currentPage]);

  const toggleExpand = (postId) => {
    if (expandedPosts.includes(postId)) {
      setExpandedPosts(expandedPosts.filter((id) => id !== postId));
    } else {
      setExpandedPosts([...expandedPosts, postId]);
    }
  };


  const handleNextPage = () => {
    setLoader(true);
    if (currentPage < totalPages) {
      setLoader(false);
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    setLoader(true);
    if (currentPage > 1) {
      setLoader(false);
      setCurrentPage(currentPage - 1);
    }
  };
  const handlePageClick = (page) => {
    setCurrentPage(page);
  };
  
  const dateFormat = (publishDate)=>{
    const dateString = new Date(publishDate);
    const options = { day:'numeric', year: 'numeric', month: 'long' };
    if (typeof dateString === 'object' && dateString !== null && 'toLocaleDateString' in dateString) {
      const result = dateString.toLocaleDateString('en-US',options);
      return <span>{result}</span>
    }
    
  }
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageDisplay = 5;

    // Calculate the start and end page numbers to display
    let startPage = Math.max(1, currentPage - Math.floor(maxPageDisplay / 2));
    let endPage = startPage + maxPageDisplay - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPageDisplay + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageClick(i)}
          className={currentPage === i ? 'active' : ''}
        >
          {i}
        </button>
      );
    }

    return pageNumbers;
  };

  const truncateString=(str, maxLength)=> {
    if (str.length <= maxLength) {
      return str; // No truncation needed
    }
  
    // Truncate the string and append an ellipsis
    const truncatedString = str.slice(0, maxLength - 3) + '...';
    return truncatedString;
  }
  const truncatedTitle=(title)=>{
    const originalString = title;
    const maxLength = 90;
    return truncateString(originalString, maxLength);
  }
  const renderTags = (tags, postId) => {
    if (expandedPosts.includes(postId)) {
      return (
        <div className='text-xs text-secondary'>
          {tags.map((tag) => (
            <span className='bg-[#EFEFF4] rounded-[40px] px-[6px] py-[4px] mb-xsp mr-xsp inline-flex text-[10px] sm:text-[14px]' key={tag.id}>
              {tag.name}
            </span>
          ))}
        </div>
      );
    } else {
      return null;
    }
  };

  if (!posts) {
    return <div className='fixed inset-0 z-[9999] items-center flex bg-[rgba(255,255,255,0.8)]'>
    <div className='w-[100px] mx-auto h-[100px] justify-center flex'>
    <ThreeDots 
      height="80" 
      width="80" 
      radius="9"
      color="#032B5C" 
      ariaLabel="three-dots-loading"
      wrapperStyle={{}}
      wrapperClassName="m-auto"
      visible={true}
      />
      </div>
    </div>;
  }
  

  return (
    <main className='mt-[100px] mb-10'>
      <Head>  
          <title>{metadata.title}</title>
          <meta name="description" content={metadata.description} />
          <meta name="keywords" content={metadata.keywords} />
          {/* OpenGraph tags */} 
          <meta property="og:title" content={metadata.openGraph.title} />
          <meta property="og:description" content={metadata.openGraph.description} />
          <meta property="og:url" content={metadata.openGraph.url} />
          <meta property="og:image" content={metadata.openGraph.imagePath} />
          <link rel="canonical" href={metadata.alternates.canonical} />
        </Head>
      <section className='pt-20 pb-10 bg-white sm:py-10'>
          <div className='container px-3 xm:px-2  lg:px-3 '>
          <ResourcesLinks />
              <ul className="flex flex-wrap flex-1 -mx-2">
                {posts.map((post) => (
                  <li key={post.id} className="w-1/4 md:w-1/2 sm:w-full p-1 list-none">
                    <div className="bg-white shadow-cardShadow2 border border-[#E6E6E6] min-h-[200px] hover:shadow-cardHover2 transition-shadow animate-fade-in">
                    <Link  href={`/events/${post.slug}`}>
                    {post._embedded && post._embedded['wp:featuredmedia'] && (
                        <Image className='w-full h-auto sm:h-auto'
                          src={post._embedded['wp:featuredmedia'][0].source_url}
                          alt={post.title.rendered} width={286} height={180}
                        />
                      )}
                    </Link>
                      <div className="px-3 pt-3">
                            {/* <span className="bg-[#FBE9EA] text-secondary text-sm p-xsp">{item.category}</span> */}
                            <Link href={`/events/${post.slug}`}>
                            <p className='text-xs text-[#7b7b7b]'>{dateFormat(post.date)}</p>
                            <h3 className="text-sm text-secondary my-1 mb-3 " dangerouslySetInnerHTML={{ __html: truncatedTitle(post.title.rendered) }} />
                            <div className='flex items-end justify-between mb-3'>
                            {/* <span className="flex item-center text-brand text-xs">
                                Read More
                                <span className="ml-1">
                                <svg xmlns="https://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" fill="rgba(2,43,92,1)"></path>
                                </svg>
                                </span>
                              </span> */}
                            </div>
                            </Link>
                            
                        </div>                              
                    </div>
                  </li>
                ))}
              </ul>
          </div>
        </section>
        {!loader &&               
        <Pagination pageNumbers={renderPageNumbers()} handlePrevPage={handlePrevPage} handleNextPage={handleNextPage} currentPage={currentPage} totalPages={totalPages}/>
        }
        {loader &&
        <div className='fixed inset-0 z-[9999] items-center flex bg-[rgba(255,255,255,0.8)]'>
          <div className='w-[100px] mx-auto h-[100px] justify-center flex'>
          <ThreeDots 
            height="80" 
            width="80" 
            radius="9"
            color="#032B5C" 
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClassName="m-auto"
            visible={true}
            />
            </div>
          </div>
      }
    </main>
  );
}

export default News;
